import { KeyboardEvent, ReactNode, useCallback } from "react";
import { Control, Controller, FieldError, FieldPath, FieldValues } from "react-hook-form";

import Error from "@components/fields/Error";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import styles from "@components/fields/RichTextArea.module.css";
import WYSIWYGEditor, { EditorSize } from "@components/fields/WYSIWYGEditor";
import TinyText from "@components/text/TinyText";
import InputLabel from "@components/typography/InputLabel";

type Props<FormData extends FieldValues> = {
  autofocus?: boolean;
  centerLabel?: boolean;
  contentRefreshSwitch?: boolean;
  control: Control<FormData>;
  disabled?: boolean;
  error: FieldError | undefined;
  extraMenuButtons?: ReactNode[];
  extraStyles?: string;
  forMessaging?: boolean;
  instructions?: string;
  journeyId: string | null;
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  placeholder?: string;
  preventEnter?: boolean;
  required?: boolean;
  sharableURLSHidden?: boolean;
  sharableURLSWithoutJourneyId?: boolean;
  size?: EditorSize;
  submitButtonSpacing?: boolean;
  tabIndex?: number;
  templateKeywords?: boolean;
  textMenuHidden?: boolean;
  toolbarHidden?: boolean;
};

const RichTextArea = <FormData extends FieldValues>({
  autofocus = false,
  centerLabel = false,
  contentRefreshSwitch = false,
  control,
  disabled = false,
  error,
  extraMenuButtons,
  extraStyles,
  forMessaging = false,
  instructions,
  journeyId,
  label,
  name,
  placeholder,
  preventEnter = false,
  required,
  sharableURLSHidden = false,
  sharableURLSWithoutJourneyId = false,
  size = "normal",
  submitButtonSpacing = false,
  templateKeywords = false,
  textMenuHidden = false,
  toolbarHidden = false
}: Props<FormData>) => {
  // We want forms to submit themselves on enter keypresses, but enters are allowed within a text area.
  // This stops propagation of those events so they don't reach the form.
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.stopPropagation();
    }
  }, []);

  return (
    <>
      <div className={styles.wrapperItem} onKeyDown={handleKeyDown}>
        {label && (
          <div className={centerLabel ? styles.centerLabel : styles.labels}>
            <div data-test="label">
              <InputLabel>
                {label}
                {required && <RequiredFieldIndicator />}
              </InputLabel>
            </div>
          </div>
        )}
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <WYSIWYGEditor
              autofocus={autofocus}
              contentRefreshSwitch={contentRefreshSwitch}
              disabled={disabled}
              extraMenuButtons={extraMenuButtons}
              extraStyles={extraStyles}
              forMessaging={forMessaging}
              journeyId={journeyId}
              onChange={onChange}
              placeholder={placeholder}
              preventEnter={preventEnter}
              sharableURLSHidden={sharableURLSHidden}
              sharableURLSWithoutJourneyId={sharableURLSWithoutJourneyId}
              size={size}
              submitButtonSpacing={submitButtonSpacing}
              templateKeywords={templateKeywords}
              textMenuHidden={textMenuHidden}
              toolbarHidden={toolbarHidden}
              value={value as string | undefined}
            />
          )}
        />
      </div>
      {(instructions || error) && (
        <div>
          {instructions && <TinyText>{instructions}</TinyText>}
          {error && (
            <div className={styles.errorWrapper}>
              <Error error={error} />
            </div>
          )}
        </div>
      )}
    </>
  );
};
export default RichTextArea;
